<template>
  <step-template :subStep="subStep">
    <v-card-text v-if="hasValidQuestionSet">
      <!-- Banner Image -->
      <v-row v-if="resolvedBannerSrc">
        <v-col>
          <img
            :lazy-src="resolvedBannerSrc"
            contain
            :src="resolvedBannerSrc"
            style="margin: 0 auto; max-height: 200px;"
          />
        </v-col>
      </v-row>

      <!-- Question -->
      <h4 class="pt-8 pb-sm-8 px-sm-4 text-center" v-html="resolvedQuestion"></h4>

      <!-- Answer Options (shown when not submitted OR no correct answer defined) -->
      <v-radio-group v-if="showOptions" v-model="selected">
        <v-alert
          v-for="(option, key) in resolvedOptions"
          :key="key"
          class="mx-md py-0 px-5"
          :border="borderPosition"
          colored-border
          :color="cColor(key)"
          elevation="2"
          v-ripple="{ center: true }"
        >
          <v-row class="cursor-pointer" @click="selectOption(option)">
            <v-col cols="1" class="mt-1">
              <v-radio
                :value="option.value"
                :color="cColor(key)"
                :ripple="false"
                off-icon="mdi-checkbox-blank-circle-outline"
                on-icon="mdi-check-circle-outline"
              ></v-radio>
            </v-col>
            <v-col cols="11">
              <div v-if="option.title" class="text-h6" v-html="option.title"></div>
              <div v-if="option.content" v-html="option.content"></div>
            </v-col>
          </v-row>
        </v-alert>
      </v-radio-group>

      <!-- Confidence Question (shown when confidence is enabled and not submitted) -->
      <div v-if="resolvedConfidence && showOptions" class="confidence-section mt-1 mx-4">
        <!-- <v-divider class="mb-4" style="border-style: dashed;"></v-divider> -->
        <p class="text-body-1 mb-3">
          Please select the extent to which you feel confident in your answer: <span class="red--text">*</span>
        </p>
        <!-- Desktop: horizontal layout (md and up) -->
        <v-radio-group v-model="selectedConfidence" row class="confidence-options mt-0 d-none d-md-flex">
          <v-row justify="space-between" no-gutters>
            <v-col 
              v-for="(conf, index) in confidenceOptions" 
              :key="index"
              cols="auto"
              class="text-center"
            >
              <v-radio
                :value="conf.value"
                :label="conf.label"
                class="confidence-radio"
                color="primary"
              ></v-radio>
            </v-col>
          </v-row>
        </v-radio-group>
        <!-- Mobile/Tablet: vertical layout (below md) -->
        <v-radio-group v-model="selectedConfidence" class="confidence-options-mobile mt-0 d-flex d-md-none">
          <v-radio
            v-for="(conf, index) in confidenceOptions"
            :key="index"
            :value="conf.value"
            :label="conf.label"
            color="primary"
            class="mb-2"
          ></v-radio>
        </v-radio-group>
      </div>

      <!-- Previously Completed Message (shown when returning to a completed question) -->
      <v-row v-if="showPreviouslyCompleted" style="width: 100%;">
        <v-col>
          <v-sheet
            class="pa-6 mx-8 mt-2 mb-0 text-center"
            elevation="3"
            rounded
            :color="previouslyCompletedBoxColor"
          >
            <v-icon color="white" size="32" class="mb-2">mdi-check-circle</v-icon>
            <h5 class="text-break white--text">
              You have already completed this question.
            </h5>
            <div class="white--text mt-5 mb-0">
              <span style="opacity: 0.8; font-size: 1.2em;">Your answer:</span>
              <p :style="{ fontSize: previousAnswerFontSize }" class="white--text mt-1 mb-0 font-weight-bold" v-html="previousAnswerDisplay"></p>
            </div>
            <h6
              v-if="rationaleMessage"
              class="text-break white--text rationale-box"
            ><strong>Rationale: </strong><span v-html="rationaleMessage"></span></h6>
            <p
              v-if="referenceMessage"
              class="text-break white--text mt-3 mb-0 reference-text"
            ><strong>Reference: </strong><span v-html="referenceMessage"></span></p>
          </v-sheet>
        </v-col>
      </v-row>

      <!-- Incorrect Answer Box (shown when incorrect and no rationale) -->
      <v-row v-if="showIncorrectWithoutRationale" style="width: 100%;">
        <v-col>
          <v-sheet
            class="pa-6 mx-8 mt-5 mb-0 text-center"
            elevation="3"
            rounded
            color="orange"
          >
            <v-icon color="white" size="32">mdi-close-circle</v-icon>
            <h5 class="text-break white--text mt-2">
              Incorrect
            </h5>
            <div class="white--text mt-5 mb-0">
              <span style="opacity: 0.8; font-size: 1.2em;">Your answer:</span>
              <p :style="{ fontSize: previousAnswerFontSize }" class="white--text mt-1 mb-0 font-weight-bold" v-html="previousAnswerDisplay"></p>
            </div>
          </v-sheet>
        </v-col>
      </v-row>

      <!-- Correct Answer Box (shown when correct and no rationale) -->
      <v-row v-if="showCorrectWithoutRationale" style="width: 100%;">
        <v-col>
          <v-sheet
            class="pa-6 mx-8 mt-5 mb-0 text-center"
            elevation="3"
            rounded
            color="green"
          >
            <v-icon color="white" size="32">mdi-check-circle</v-icon>
            <h5 class="text-break white--text mt-2">
              Correct
            </h5>
            <div class="white--text mt-5 mb-0">
              <span style="opacity: 0.8; font-size: 1.2em;">Your answer:</span>
              <p :style="{ fontSize: previousAnswerFontSize }" class="white--text mt-1 mb-0 font-weight-bold" v-html="previousAnswerDisplay"></p>
            </div>
          </v-sheet>
        </v-col>
      </v-row>

      <!-- Rationale Message Box (shown after submission, not for previously completed) -->
      <v-row v-if="showRationale && !previouslyCompleted" style="width: 100%;">
        <v-col>
          <v-sheet
            class="pa-6 mx-8 mt-5 mb-0 text-center"
            elevation="3"
            rounded
            :color="rationaleBoxBgColor"
          >
            <!-- Incorrect indicator -->
            <div v-if="hasCorrectAnswer && !isCorrect" class="mb-3">
              <v-icon color="white" size="32">mdi-close-circle</v-icon>
              <h5 class="text-break white--text mt-2">
                Incorrect
              </h5>
            </div>
            <!-- Correct indicator -->
            <div v-if="hasCorrectAnswer && isCorrect" class="mb-3">
              <v-icon color="white" size="32">mdi-check-circle</v-icon>
              <h5 class="text-break white--text mt-2">
                Correct
              </h5>
            </div>
            <div class="mt-5 mb-3" :style="`color:${rationaleBoxTxtColor};`">
              <span style="opacity: 0.8; font-size: 1.2em;">Your answer:</span>
              <p :style="{ fontSize: previousAnswerFontSize, color: rationaleBoxTxtColor }" class="mt-1 mb-0 font-weight-bold" v-html="previousAnswerDisplay"></p>
            </div>
            <h6
              class="text-break rationale-box"
              :style="`color:${rationaleBoxTxtColor}`"
            ><strong>Rationale: </strong><span v-html="rationaleMessage"></span></h6>
            <p
              v-if="referenceMessage"
              class="text-break mt-3 mb-0 reference-text"
              :style="`color:${rationaleBoxTxtColor}`"
            ><strong>Reference: </strong><span v-html="referenceMessage"></span></p>
          </v-sheet>
        </v-col>
      </v-row>

      <!-- Answer Box for no-correct-answer mode without rationale -->
      <v-row v-if="showAnswerWithoutRationale" style="width: 100%;">
        <v-col>
          <v-sheet
            class="pa-6 mx-8 mt-5 mb-0 text-center"
            elevation="3"
            rounded
            :color="rationaleBoxBgColor"
          >
            <div class="mt-0 mb-0" :style="`color:${rationaleBoxTxtColor};`">
              <span style="opacity: 0.8; font-size: 1.2em;">Your answer:</span>
              <p :style="{ fontSize: previousAnswerFontSize, color: rationaleBoxTxtColor }" class="mt-1 mb-0 font-weight-bold" v-html="previousAnswerDisplay"></p>
            </div>
          </v-sheet>
        </v-col>
      </v-row>

      <!-- Message Below (shown after submission) -->
      <v-row v-if="submitted && resolvedMessageBelow" style="width: 100%;">
        <v-col>
          <h5 class="text-center text-break mx-8 mt-5 mb-0" v-html="resolvedMessageBelow"></h5>
        </v-col>
      </v-row>

      <!-- Submit Answer Button (shown before submission when option selected) -->
      <v-row v-if="showSubmitButton" class="justify-center mt-5">
        <v-col cols="auto">
          <v-btn
            class="white--text font-weight-bold"
            x-large
            elevation="2"
            rounded
            color="primary"
            @click="submitAnswer"
          >
            Submit Answer
          </v-btn>
        </v-col>
      </v-row>

      <!-- Retry Button (shown when answer is incorrect) -->
      <v-row v-if="showRetryButton" class="justify-center mt-5">
        <v-col cols="auto">
          <v-btn
            class="white--text font-weight-bold"
            x-large
            elevation="2"
            rounded
            color="orange"
            @click="retryAnswer"
          >
            Retry My Answer
          </v-btn>
        </v-col>
      </v-row>

      <!-- Continue Message (shown when answer is correct or no correct answer) -->
      <v-row v-if="showContinueMessage" style="width: 100%;">
        <v-col>
          <h5 class="text-center text-break mx-8 mt-5 mb-0">
            Click <span style="color:#ff9800;"><b>CONTINUE</b></span> to proceed.
          </h5>
        </v-col>
      </v-row>
    </v-card-text>
  </step-template>
</template>

<script>
/**
 * To use:
 - in the database, go to table substeps
 - find the substep row that you want this component
 - update the row field component='step-pre-post'
 - update the row field component_props as a JSON like:

* QUESTION WITH CORRECT ANSWER (assessment mode)
{
  "variables": { "vmodel": "question1_answer" },
  "question": "What is the correct treatment approach?",
  "options": [
    {
      "value": "1",
      "title": "Option A",
      "content": "Description of option A",
      "rationale": "This is incorrect because..."
    },
    {
      "value": "2",
      "title": "Option B",
      "content": "Description of option B",
      "correct": true,
      "rationale": "This is correct because..."
    },
    {
      "value": "3",
      "title": "Option C",
      "content": "Description of option C",
      "rationale": "This is incorrect because..."
    }
  ]
}

OR

* QUESTION WITHOUT CORRECT ANSWER (survey/opinion mode)
{
  "variables": { "vmodel": "survey_answer" },
  "question": "What is your preferred approach?",
  "options": [
    {
      "value": "1",
      "title": "Approach A",
      "content": "Description",
      "rationale": "Additional information about this choice..."
    },
    {
      "value": "2",
      "title": "Approach B",
      "content": "Description",
      "rationale": "Additional information about this choice..."
    }
  ]
}

OR

* WITH CUSTOM MESSAGE BOX COLORS (for no-correct-answer mode)
{
  "variables": { "vmodel": "survey_answer" },
  "question": "What is your opinion?",
  "messageBoxBgColor": "blue",
  "messageBoxTxtColor": "white",
  "messageBelow": "Thank you for your response.",
  "options": [...]
}

* STYLING OPTIONS
{
  "color": "orange",
  "colorAccent": false,
  "borderPosition": "left",
  "bannerSrc": "/img/banner.jpg"
}

* WITH CONFIDENCE QUESTION
{
  "variables": { "vmodel": "question1_answer" },
  "question": "What is the correct treatment approach?",
  "confidence": true,
  "options": [
    { "value": "1", "title": "Option A", "correct": true, "rationale": "Correct because..." },
    { "value": "2", "title": "Option B", "rationale": "Incorrect because..." }
  ]
}
// Log format with confidence:
// With correct answer: "PrePost Answered: variable_name: answer_value: Fairly Confident: correct"
// Without correct answer: "PrePost Answered: variable_name: answer_value: Fairly Confident"

* CONDITIONAL QUESTION SETS - Based on Vuex data (avatar, user, caseData)
// Shows different questions based on a variable value. Supports "default" fallback.
// If no match and no default, nothing is displayed.

// Example: Different question based on avatar id
{
  "variables": { "vmodel": "question1_answer" },
  "questionSets": {
    "dataset": "avatar",
    "variable": "id",
    "sets": {
      "3": {
        "question": "Question for avatar 3 (Nico)?",
        "confidence": true,
        "options": [
          { "value": "A", "title": "A. Option for Nico", "rationale": "..." },
          { "value": "B", "title": "B. Another option", "correct": true, "rationale": "..." }
        ]
      },
      "4": {
        "question": "Question for avatar 4?",
        "confidence": false,
        "options": [
          { "value": "A", "title": "A. Option A", "correct": true, "rationale": "..." },
          { "value": "B", "title": "B. Option B", "rationale": "..." }
        ]
      },
      "default": {
        "question": "Default question for other avatars?",
        "confidence": true,
        "options": [
          { "value": "A", "title": "A. Default option", "correct": true, "rationale": "..." }
        ]
      }
    }
  }
}

// Example: Root-level defaults with per-set overrides
// Root properties act as defaults; individual sets can override them
{
  "variables": { "vmodel": "question1_answer" },
  "confidence": true,
  "bannerSrc": "/images/default_banner.jpg",
  "questionSets": {
    "dataset": "caseData",
    "variable": "patientType",
    "sets": {
      "diabetic": {
        "question": "Question for diabetic patients?",
        "bannerSrc": "/images/diabetic_banner.jpg",
        "options": [...]
      },
      "cardiac": {
        "question": "Question for cardiac patients?",
        "confidence": false,
        "options": [...]
      },
      "default": {
        "question": "Default question?",
        "options": [...]
      }
    }
  }
}

// Example: CaseData shorthand (omit dataset, defaults to caseData)
{
  "variables": { "vmodel": "question1_answer" },
  "questionSets": {
    "variable": "treatmentChoice",
    "sets": {
      "medication": { "question": "...", "options": [...] },
      "surgery": { "question": "...", "options": [...] },
      "default": { "question": "...", "options": [...] }
    }
  }
}

// Supported datasets: "caseData" (default), "avatar", "user"
// If no match found and no "default" set exists, nothing is displayed
 */
import { mapState, mapMutations, mapActions } from "vuex";
import StepTemplate from "/src/components/steps/StepTemplate.vue";

export default {
  name: "StepPrePost",
  components: {
    StepTemplate,
  },
  props: {
    steps: {
      type: Array,
      required: true,
    },
    step: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    subStep: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    question: {
      type: String,
      required: false,
      default: "",
    },
    options: {
      type: Array,
      required: false,
      default: () => {
        return [];
      },
    },
    variables: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
    color: {
      type: String,
      required: false,
      default: "orange",
    },
    colorAccent: {
      type: Boolean,
      required: false,
      default: true,
    },
    borderPosition: {
      type: String,
      required: false,
      default: "left",
    },
    bannerSrc: {
      type: String,
      default: "",
      required: false,
    },
    messageBoxBgColor: {
      type: String,
      required: false,
      default: "",
    },
    messageBoxTxtColor: {
      type: String,
      required: false,
      default: "",
    },
    messageBelow: {
      type: String,
      required: false,
      default: "",
    },
    confidence: {
      type: Boolean,
      required: false,
      default: false,
    },
    questionSets: {
      type: Object,
      required: false,
      default: null,
    },
    reference: {
      type: String,
      required: false,
      default: "",
    },
  },
  data() {
    return {
      selected: null,
      submitted: false,
      isCorrect: false,
      selectedOption: null,
      selectedConfidence: null,
      previouslyCompleted: false,
      confidenceOptions: [
        { value: "Completely Confident", label: "Completely Confident" },
        { value: "Fairly Confident", label: "Fairly Confident" },
        { value: "Somewhat Confident", label: "Somewhat Confident" },
        { value: "Slightly Confident", label: "Slightly Confident" },
        { value: "Not Confident", label: "Not Confident" },
      ],
    };
  },
  computed: {
    ...mapState({
      caseData: (state) => state.caseData,
      avatar: (state) => state.avatar,
      user: (state) => state.user,
    }),
    hasValidQuestionSet() {
      // If no questionSets defined, we're in simple mode - always valid
      if (!this.questionSets) return true;
      // If questionSets defined, check if we have a resolved set
      return this.activeQuestionSet !== null;
    },
    activeQuestionSet() {
      // If no questionSets defined, return null (simple mode)
      if (!this.questionSets) return null;
      
      // Get the dataset and variable to switch on
      const dataSet = this.getDataSet(this.questionSets);
      const variableValue = this.getVariableValue(dataSet, this.questionSets.variable);
      
      // If no variable value found, try default
      if (variableValue === undefined || variableValue === null) {
        return this.questionSets.sets?.default || null;
      }
      
      // Look for matching set, fall back to default
      const sets = this.questionSets.sets;
      if (!sets) return null;
      
      // Convert variableValue to string for key lookup
      const key = String(variableValue);
      return sets[key] || sets.default || null;
    },
    resolvedQuestion() {
      if (this.activeQuestionSet) {
        return this.activeQuestionSet.question || this.question;
      }
      return this.question;
    },
    resolvedOptions() {
      if (this.activeQuestionSet) {
        return this.activeQuestionSet.options || this.options;
      }
      return this.options;
    },
    resolvedConfidence() {
      if (this.activeQuestionSet && this.activeQuestionSet.confidence !== undefined) {
        return this.activeQuestionSet.confidence;
      }
      return this.confidence;
    },
    resolvedBannerSrc() {
      if (this.activeQuestionSet && this.activeQuestionSet.bannerSrc !== undefined) {
        return this.activeQuestionSet.bannerSrc;
      }
      return this.bannerSrc;
    },
    resolvedMessageBoxBgColor() {
      if (this.activeQuestionSet && this.activeQuestionSet.messageBoxBgColor !== undefined) {
        return this.activeQuestionSet.messageBoxBgColor;
      }
      return this.messageBoxBgColor;
    },
    resolvedMessageBoxTxtColor() {
      if (this.activeQuestionSet && this.activeQuestionSet.messageBoxTxtColor !== undefined) {
        return this.activeQuestionSet.messageBoxTxtColor;
      }
      return this.messageBoxTxtColor;
    },
    resolvedMessageBelow() {
      if (this.activeQuestionSet && this.activeQuestionSet.messageBelow !== undefined) {
        return this.activeQuestionSet.messageBelow;
      }
      return this.messageBelow;
    },
    cColor() {
      return (key) => {
        let accent;
        if (this.colorAccent) accent = `accent-${key + 1}`;
        return `${this.color} ${accent}`;
      };
    },
    hasCorrectAnswer() {
      return this.resolvedOptions.some((option) => option.correct === true);
    },
    correctOption() {
      return this.resolvedOptions.find((option) => option.correct === true);
    },
    showOptions() {
      // Show options if not submitted, or if submitted but incorrect (will be hidden during retry reset)
      return !this.submitted;
    },
    showSubmitButton() {
      // If confidence is required, both answer and confidence must be selected
      if (this.resolvedConfidence) {
        return this.selected !== null && this.selectedConfidence !== null && !this.submitted;
      }
      return this.selected !== null && !this.submitted;
    },
    showRetryButton() {
      return this.submitted && this.hasCorrectAnswer && !this.isCorrect;
    },
    showIncorrectWithoutRationale() {
      // Show incorrect box when:
      // - Submitted and incorrect
      // - No rationale to display
      // - Not previously completed (returning to page)
      return this.submitted && 
             this.hasCorrectAnswer && 
             !this.isCorrect && 
             !this.showRationale &&
             !this.previouslyCompleted;
    },
    showCorrectWithoutRationale() {
      // Show correct box when:
      // - Submitted and correct
      // - No rationale to display
      // - Not previously completed (returning to page)
      return this.submitted && 
             this.hasCorrectAnswer && 
             this.isCorrect && 
             !this.showRationale &&
             !this.previouslyCompleted;
    },
    showPreviouslyCompleted() {
      // Show "already completed" message when:
      // - Previously completed (loaded from caseData)
      // - Answer was correct OR no correct answer defined
      return this.previouslyCompleted && (this.isCorrect || !this.hasCorrectAnswer);
    },
    previousAnswerDisplay() {
      // Display the title of the selected option, or fall back to value
      if (this.selectedOption) {
        return this.selectedOption.title || this.selectedOption.content || this.selectedOption.value;
      }
      return "";
    },
    previousAnswerFontSize() {
      const len = this.previousAnswerDisplay.length;
      if (len <= 10) return '2em';
      if (len <= 25) return '1.5em';
      return '1.2em';
    },
    showContinueMessage() {
      // Show continue message when:
      // 1. Submitted and correct (assessment mode)
      // 2. Submitted and no correct answer defined (survey mode)
      return this.submitted && (this.isCorrect || !this.hasCorrectAnswer);
    },
    showRationale() {
      // Show rationale box if submitted and the selected option has a rationale
      return this.submitted && this.selectedOption && this.selectedOption.rationale;
    },
    showAnswerWithoutRationale() {
      // Show answer box when:
      // - Submitted and no correct answer (survey mode)
      // - No rationale to display
      // - Not previously completed
      return this.submitted && 
             !this.hasCorrectAnswer && 
             !this.showRationale &&
             !this.previouslyCompleted;
    },
    rationaleMessage() {
      if (!this.selectedOption) return "";
      return this.selectedOption.rationale || "";
    },
    referenceMessage() {
      if (!this.selectedOption) return "";
      return this.selectedOption.reference || "";
    },
    rationaleBoxBgColor() {
      // If custom colors provided (for survey mode), use them
      if (this.resolvedMessageBoxBgColor) {
        return this.resolvedMessageBoxBgColor;
      }
      // Otherwise use correct/incorrect defaults
      if (this.hasCorrectAnswer) {
        return this.isCorrect ? "green" : "orange";
      }
      // No correct answer defined (survey mode) - default to blue
      return "blue";
    },
    rationaleBoxTxtColor() {
      // If custom colors provided, use them
      if (this.resolvedMessageBoxTxtColor) {
        return this.resolvedMessageBoxTxtColor;
      }
      // Default to white
      return "white";
    },
    previouslyCompletedBoxColor() {
      // Green if question had a correct answer, blue if no correct answer (survey mode)
      return this.hasCorrectAnswer ? "green" : "blue";
    },
  },
  created() {
    this.init();
  },
  methods: {
    ...mapMutations(["setContinueButtonEnabled"]),
    ...mapActions(["setCaseDataValue", "trackingAction"]),
    getDataSet(config) {
      if (config.dataset === undefined || config.dataset === null || config.dataset === "caseData") {
        return this.caseData;
      } else if (config.dataset === "user") {
        return this.user;
      } else if (config.dataset === "avatar") {
        return this.avatar;
      }
      return this.caseData;
    },
    getVariableValue(dataSet, variableName) {
      if (!dataSet || !variableName) return undefined;
      return dataSet[variableName];
    },
    init() {
      const hasPreviousState = this.loadPreviousState();
      // Only disable continue button if no previous completed state was loaded
      if (!hasPreviousState) {
        this.setContinueButtonEnabled(false);
      }
    },
    loadPreviousState() {
      // If using questionSets and no valid set resolved, return false
      if (this.questionSets && !this.hasValidQuestionSet) return false;

      const variableName = this.variables.vmodel;
      const previousValue = this.caseData[variableName];
      const previousConfidence = this.caseData[`${variableName}_confidence`];
      const wasSubmitted = this.caseData[`${variableName}_submitted`];

      if (!previousValue) return false;

      // Find the previously selected option
      const previousOption = this.resolvedOptions.find((option) => option.value == previousValue);
      if (!previousOption) return false;

      // Restore selection state
      this.selected = previousOption.value;
      this.selectedOption = previousOption;

      // Restore confidence if it was set
      if (previousConfidence) {
        this.selectedConfidence = previousConfidence;
      }

      // If they had submitted before, restore the submitted state
      if (wasSubmitted) {
        this.submitted = true;

        // Determine if answer was correct
        if (this.hasCorrectAnswer) {
          this.isCorrect = previousOption.correct === true;
          // Mark as previously completed if correct
          if (this.isCorrect) {
            this.previouslyCompleted = true;
          }
          // Enable continue only if correct
          this.setContinueButtonEnabled(this.isCorrect);
          return true; // Previous state was loaded and continue button was set
        } else {
          // No correct answer - mark as previously completed and enable continue
          this.previouslyCompleted = true;
          this.setContinueButtonEnabled(true);
          return true; // Previous state was loaded and continue button was set
        }
      }

      // Previous value exists but wasn't submitted - don't enable continue
      return false;
    },
    selectOption(option) {
      this.selected = option.value;
      this.selectedOption = option;
    },
    submitAnswer() {
      if (!this.selectedOption) return;

      // Save the answer to caseData
      this.setCaseDataValue({
        key: this.variables.vmodel,
        value: this.selectedOption.value,
        fullObject: this.selectedOption,
      });

      // Save confidence to caseData if enabled
      if (this.confidence && this.selectedConfidence) {
        this.setCaseDataValue({
          key: `${this.variables.vmodel}_confidence`,
          value: this.selectedConfidence,
        });
      }

      // Save submitted state to caseData for persistence
      this.setCaseDataValue({
        key: `${this.variables.vmodel}_submitted`,
        value: true,
      });

      this.submitted = true;

      // Build log message
      let logMessage = `PrePost Answered: ${this.variables.vmodel}: ${this.selectedOption.value}`;
      
      // Add confidence if enabled
      if (this.confidence && this.selectedConfidence) {
        logMessage += `: ${this.selectedConfidence}`;
      }

      // Check if answer is correct (if there's a correct answer defined)
      if (this.hasCorrectAnswer) {
        this.isCorrect = this.selectedOption.correct === true;
        const status = this.isCorrect ? "correct" : "incorrect";
        logMessage += `: ${status}`;
        this.trackingAction(logMessage);
        // Enable continue only if correct
        this.setContinueButtonEnabled(this.isCorrect);
      } else {
        // No correct answer - log and enable continue
        this.trackingAction(logMessage);
        this.isCorrect = false;
        this.setContinueButtonEnabled(true);
      }
    },
    retryAnswer() {
      // Reset state for retry
      this.selected = null;
      this.selectedOption = null;
      this.selectedConfidence = null;
      this.submitted = false;
      this.isCorrect = false;
      this.setContinueButtonEnabled(false);

      // Clear submitted flag from caseData so retry state is persisted
      this.setCaseDataValue({
        key: `${this.variables.vmodel}_submitted`,
        value: false,
      });
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.confidence-section {
  background-color: #f2faff;
  border: 1px solid #b2d8f6;
  border-radius: 8px;
  padding: 18px 32px 4px;
}

.confidence-options .v-radio {
  margin-right: 0;
}

.confidence-radio {
  flex-direction: column;
  align-items: center;
}

.confidence-radio >>> .v-label {
  font-size: 0.75em;
  text-align: center;
  margin-top: 4px;
  white-space: nowrap;
}

.confidence-radio >>> .v-input--selection-controls__input {
  margin-right: 0;
}

.reference-text {
  font-size: 0.9em;
  font-weight: 400;
}

.reference-text strong {
  font-weight: 700;
}

.rationale-box {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 15px 10px;
  margin-top: 20px;
  font-weight: 400;
}
</style>
